import { transparentize } from 'polished';
import c from '../colors';
import { setAlpha } from '../utils';

/**
 * @see http://livingston.sas.com/10-0/essentials/primary/
 */
export const statePrimary = {
  normal(theme) {
    switch (theme.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        return {
          fg: c.p2(theme),
          bg: c.black(theme),
          bd: c.p2(theme)
        };
      case 'light2025':
      case 'dark2025':
      case 'highcontrast2020':
        return {
          fg: theme.tokens?.['color-action-text-emphasis-normal'],
          bg: theme.tokens?.['color-action-background-emphasis-normal'],
          bd: theme.tokens?.['color-action-border-emphasis-normal']
        };
    }
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: c.p2(theme),
          bg: transparentize(0.9, c.p2(theme)),
          bd: c.p2(theme)
        };
      case 'high-contrast':
        return {
          fg: c.p3(theme),
          bg: c.black(theme),
          bd: c.p3(theme)
        };
      default:
        return {
          fg: c.white(theme),
          bg: c.p(theme),
          bd: c.p(theme)
        };
    }
  },
  hover(theme) {
    switch (theme.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        return {
          fg: c.p2(theme),
          bg: transparentize(0.7, c.p2(theme)),
          bd: c.p2(theme)
        };
      case 'marine':
      case 'sail':
        return {
          fg: c.p2(theme),
          bg: c.p(theme),
          bd: c.p(theme)
        };
      case 'light2025':
      case 'dark2025':
      case 'highcontrast2020':
        return {
          fg: theme.tokens?.['color-action-text-emphasis-hover'],
          bg: theme.tokens?.['color-action-background-emphasis-hover'],
          bd: theme.tokens?.['color-action-border-emphasis-hover']
        };
    }
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: c.p2(theme),
          bg: transparentize(0.8, c.p2(theme)),
          bd: c.p2(theme)
        };
      case 'high-contrast':
        return {
          fg: c.p3(theme),
          bg: c.n_3(theme),
          bd: c.p3(theme)
        };
      default:
        return {
          fg: transparentize(0.5, c.white(theme)),
          bg: c.p(theme),
          bd: c.p(theme)
        };
    }
  },
  active(theme) {
    switch (theme.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        return {
          fg: c.black(theme),
          bg: c.p2(theme),
          bd: 'transparent'
        };
      case 'marine':
      case 'sail':
        return {
          fg: c.p1(theme),
          bg: c.p_1(theme),
          bd: c.p_1(theme)
        };
      case 'light2025':
      case 'dark2025':
      case 'highcontrast2020':
        return {
          fg: theme.tokens?.['color-action-text-emphasis-active'],
          bg: theme.tokens?.['color-action-background-emphasis-active'],
          bd: theme.tokens?.['color-action-border-emphasis-active']
        };
    }
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: c.p2(theme),
          bg: transparentize(0.75, c.p2(theme)),
          bd: c.p2(theme)
        };
      case 'high-contrast':
        return {
          fg: c.black(theme),
          bg: c.p3(theme),
          bd: 'transparent'
        };
      default:
        return {
          fg: transparentize(0.5, c.white(theme)),
          bg: c.p_1(theme),
          bd: c.p_1(theme)
        };
    }
  },
  selected(theme) {
    switch (theme.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        return {
          fg: c.black(theme),
          bg: c.p2(theme),
          bd: 'transparent'
        };
      case 'light2025':
      case 'dark2025':
      case 'highcontrast2020':
        return {
          fg: theme.tokens?.['color-action-text-emphasis-selected'],
          bg: theme.tokens?.['color-action-background-emphasis-selected'],
          bd: theme.tokens?.['color-action-border-emphasis-selected']
        };
    }
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: c.p2(theme),
          bg: transparentize(0.75, c.p2(theme)),
          bd: c.p2(theme)
        };
      case 'high-contrast':
        return {
          fg: c.black(theme),
          bg: c.p3(theme),
          bd: 'transparent'
        };
      default:
        return {
          fg: c.white(theme),
          bg: c.p_1(theme),
          bd: c.p_1(theme)
        };
    }
  },
  disabled(theme) {
    switch (theme.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        return {
          fg: c.hcDisabled(theme),
          bg: c.black(theme),
          bd: c.hcDisabled(theme)
        };
      case 'marine':
      case 'sail':
        return {
          fg: transparentize(0.35, c.white(theme)),
          bg: c.n(theme),
          bd: c.n2(theme)
        };
      case 'light2025':
      case 'dark2025':
      case 'highcontrast2020':
        return {
          fg: theme.tokens?.['color-action-text-emphasis-disabled'],
          bg: theme.tokens?.['color-action-background-emphasis-disabled'],
          bd: theme.tokens?.['color-action-border-emphasis-disabled']
        };
    }
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: transparentize(0.8, c.p2(theme)),
          bg: 'transparent',
          bd: transparentize(0.8, c.p2(theme))
        };
      case 'high-contrast':
        return {
          fg: transparentize(0.5, c.p3(theme)),
          bg: transparentize(0.5, c.n_3(theme)),
          bd: transparentize(0.5, c.p3(theme))
        };
      default:
        return {
          fg: transparentize(0.4, c.white(theme)),
          bg: transparentize(0.25, c.n1(theme)),
          bd: 'transparent'
        };
    }
  }
};

/**
 * @see http://livingston.sas.com/10-0/essentials/primary/
 */
export const statePrimaryContent = {
  normal(theme) {
    switch (theme.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        return {
          fg: c.p2(theme),
          bg: c.black(theme)
        };
      case 'light2025':
      case 'dark2025':
      case 'highcontrast2020':
        return {
          fg: theme.tokens?.['color-text-primary'],
          bg: theme.tokens?.['color-action-background-subtle-normal'],
          bd: 'transparent'
        };
    }
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: transparentize(0.8, c.p2(theme)),
          bg: 'transparent'
        };
      case 'high-contrast':
        return {
          fg: c.p3(theme),
          bg: 'transparent'
        };
      default:
        return {
          fg: c.white(theme),
          bg: 'transparent'
        };
    }
  },
  hover(theme) {
    switch (theme.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        return {
          fg: c.p2(theme),
          bg: transparentize(0.7, c.p2(theme))
        };
      case 'light2025':
      case 'dark2025':
      case 'highcontrast2020':
        return {
          fg: theme.tokens?.['color-action-text-emphasis-hover'],
          bg: theme.tokens?.['color-action-background-emphasis-hover'],
          bd: 'transparent'
        };
    }
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: c.p2(theme),
          bg: transparentize(0.8, c.p2(theme))
        };
      case 'high-contrast':
        return {
          fg: c.p3(theme),
          bg: c.n_3(theme)
        };
      default:
        return {
          fg: transparentize(0.5, c.white(theme)),
          bg: 'transparent'
        };
    }
  },
  active(theme) {
    switch (theme.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        return {
          fg: c.black(theme),
          bg: c.p2(theme)
        };
      case 'light2025':
      case 'dark2025':
      case 'highcontrast2020':
        return {
          fg: theme.tokens?.['color-action-text-emphasis-active'],
          bg: theme.tokens?.['color-action-background-emphasis-active'],
          bd: 'transparent'
        };
    }
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: c.p2(theme),
          bg: transparentize(0.75, c.p2(theme))
        };
      case 'high-contrast':
        return {
          fg: c.black(theme),
          bg: c.p3(theme)
        };
      default:
        return {
          fg: transparentize(0.5, c.white(theme)),
          bg: c.p_1(theme)
        };
    }
  },
  selected(theme) {
    switch (theme.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        return {
          fg: c.black(theme),
          bg: c.p2(theme)
        };
      case 'light2025':
      case 'dark2025':
      case 'highcontrast2020':
        return {
          fg: theme.tokens?.['color-action-text-emphasis-selected'],
          bg: theme.tokens?.['color-action-background-emphasis-selected'],
          bd: 'transparent'
        };
    }
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: c.p2(theme),
          bg: transparentize(0.75, c.p2(theme))
        };
      case 'high-contrast':
        return {
          fg: c.black(theme),
          bg: c.p3(theme)
        };
      default:
        return {
          fg: c.white(theme),
          bg: c.p_1(theme)
        };
    }
  },
  disabled(theme) {
    switch (theme.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        return {
          fg: c.hcDisabled(theme),
          bg: c.black(theme)
        };
      case 'light2025':
      case 'dark2025':
      case 'highcontrast2020':
        return {
          fg: theme.tokens?.['color-action-text-emphasis-disabled'],
          bg: theme.tokens?.['color-action-background-emphasis-disabled'],
          bd: 'transparent'
        };
    }
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: transparentize(0.8, c.p2(theme)),
          bg: 'transparent'
        };
      case 'high-contrast':
        return {
          fg: transparentize(0.5, c.p3(theme)),
          bg: 'transparent'
        };
      default:
        return {
          fg: transparentize(0.4, c.white(theme)),
          bg: 'transparent'
        };
    }
  }
};

/**
 * Temporary doc until the new Design System site is up
 * @see https://gitlab.sas.com/novacommons/nova-tokens/-/tree/master/tokens
 */
export const statePrimaryNova = {
  normal(theme) {
    switch (theme.baseThemeName) {
      case 'dark':
      case 'high-contrast':
        return {
          fg: c.colorTextPrimary(theme),
          bg: setAlpha(c.colorPrimary(theme), 0.1),
          bd: c.colorPrimary(theme)
        };
      default:
        return {
          fg: c.colorTextInverse(theme),
          bg: c.colorPrimary(theme),
          bd: c.colorUiLight(theme)
        };
    }
  },
  hover(theme) {
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: c.colorTextPrimary(theme),
          bg: setAlpha(c.colorPrimary(theme), 0.2),
          bd: c.colorPrimary(theme)
        };
      case 'high-contrast':
        return {
          fg: c.colorTextDefault(theme),
          bg: setAlpha(c.colorPrimary(theme), 0.2),
          bd: c.colorPrimary(theme)
        };
      default:
        return {
          fg: setAlpha(c.colorTextInverse(theme), 0.8),
          bg: c.colorPrimary(theme),
          bd: c.colorUiLight(theme)
        };
    }
  },
  active(theme) {
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: c.colorTextPrimary(theme),
          bg: setAlpha(c.colorPrimary(theme), 0.25),
          bd: c.colorPrimary(theme)
        };
      case 'high-contrast':
        return {
          fg: c.colorTextInverse(theme),
          bg: c.colorPrimary(theme),
          bd: c.colorPrimary(theme)
        };
      default:
        return {
          fg: setAlpha(c.colorTextInverse(theme), 0.6),
          bg: c.colorPrimary(theme),
          bd: c.colorUiLight(theme)
        };
    }
  },
  selected(theme) {
    return statePrimaryNova.active(theme);
  },
  disabled(theme) {
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: setAlpha(c.colorTextPrimary(theme), 0.32),
          bg: setAlpha(c.colorPrimary(theme), 0.08),
          bd: 'transparent'
        };
      case 'high-contrast':
        return {
          fg: setAlpha(c.colorPrimary(theme), 0.48),
          bg: setAlpha(c.colorPrimary(theme), 0.16),
          bd: 'transparent'
        };
      default:
        return {
          fg: setAlpha(c.colorTextInverse(theme), 0.64),
          bg: setAlpha(c.colorPrimary(theme), 0.24),
          bd: 'transparent'
        };
    }
  }
};

/**
 * Temporary doc until the new Design System site is up
 * @see https://gitlab.sas.com/novacommons/nova-tokens/-/tree/master/tokens
 */
export const statePrimaryContentNova = {
  normal(theme) {
    const sourceTokens = statePrimaryNova.normal(theme);
    return {
      ...sourceTokens,
      bg: 'transparent',
      bd: 'transparent'
    };
  },
  hover(theme) {
    const sourceTokens = statePrimaryNova.hover(theme);
    return {
      ...sourceTokens,
      bd: 'transparent'
    };
  },
  active(theme) {
    const sourceTokens = statePrimaryNova.active(theme);
    return {
      ...sourceTokens,
      bd: 'transparent'
    };
  },
  disabled(theme) {
    switch (theme.baseThemeName) {
      case 'dark':
        return {
          fg: setAlpha(c.colorTextPrimary(theme), 0.32),
          bg: setAlpha(c.colorPrimary(theme), 0.08),
          bd: 'transparent'
        };
      case 'high-contrast':
        return {
          fg: setAlpha(c.colorPrimary(theme), 0.48),
          bg: setAlpha(c.colorPrimary(theme), 0.16),
          bd: 'transparent'
        };
      default:
        return {
          fg: setAlpha(c.colorTextInverse(theme), 0.64),
          bg: setAlpha(c.colorPrimary(theme), 0.24),
          bd: 'transparent'
        };
    }
  }
};
//# sourceMappingURL=primary-states.js.map